import { useEffect, useState } from "react";
import client from "@/lib/api";
import { Panel } from "@/components/Layout";
import { AlertTriangle, Heart, Car, Home, Building2, Plus } from "lucide-react";
import { toast } from "sonner";

const SECTORS = [
  { key: "life", label: "Life", icon: Heart },
  { key: "auto", label: "Auto", icon: Car },
  { key: "home", label: "Home", icon: Home },
  { key: "commercial", label: "Commercial", icon: Building2 },
];

const SEVERITY = {
  info: { color: "text-gold border-gold", label: "INFO" },
  warning: { color: "text-[#FFD700] border-[#FFD700]", label: "WARN" },
  critical: { color: "text-[#FF3B30] border-[#FF3B30]", label: "CRIT" },
};

export default function BrandSignals() {
  const [signals, setSignals] = useState([]);
  const [form, setForm] = useState({ sector: "home", headline: "", severity: "info", region: "" });

  const load = async () => {
    const { data } = await client.get("/signals");
    setSignals(data);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.headline) return toast.error("Headline required");
    try {
      await client.post("/signals", form);
      toast.success("Risk signal logged");
      setForm({ sector: "home", headline: "", severity: "info", region: "" });
      load();
    } catch {
      toast.error("Failed to log signal");
    }
  };

  return (
    <div className="space-y-6" data-testid="signals-page">
      <header>
        <div className="label-tag mb-2">/// INTEL — INDUSTRY RISK SURVEILLANCE</div>
        <h1 className="heading-display text-4xl sm:text-5xl">Risk Signals</h1>
        <p className="text-[#888] text-sm mt-2 max-w-2xl">
          Real-time intelligence feed from across the protection landscape. Hailstorms, theft
          indexes, carrier updates, and regulatory shifts — surfaced before they bite.
        </p>
      </header>

      <Panel tag="// LOG NEW" title="Manual Signal Entry" testId="signals-form-panel">
        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end" data-testid="signals-form">
          <Field label="Sector">
            <select value={form.sector} onChange={(e) => setForm({ ...form, sector: e.target.value })} className="hud-input" data-testid="sig-sector">
              {SECTORS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
          </Field>
          <Field label="Severity">
            <select value={form.severity} onChange={(e) => setForm({ ...form, severity: e.target.value })} className="hud-input" data-testid="sig-severity">
              <option value="info">Info</option>
              <option value="warning">Warning</option>
              <option value="critical">Critical</option>
            </select>
          </Field>
          <Field label="Region">
            <input value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })} className="hud-input" data-testid="sig-region" placeholder="e.g. TX, National" />
          </Field>
          <div></div>
          <div className="md:col-span-4">
            <Field label="Headline">
              <input required value={form.headline} onChange={(e) => setForm({ ...form, headline: e.target.value })} className="hud-input" data-testid="sig-headline" placeholder="e.g. Theft index up 22% in metro corridor" />
            </Field>
          </div>
          <div className="md:col-span-4 flex justify-end">
            <button type="submit" className="btn-gold inline-flex items-center gap-2" data-testid="signals-submit">
              <Plus className="w-4 h-4" /> Log Signal
            </button>
          </div>
        </form>
      </Panel>

      <Panel tag={`// FEED — ${signals.length} ACTIVE`} title="Live Intelligence" testId="signals-list">
        <div className="space-y-2">
          {signals.map((s) => {
            const Sec = SECTORS.find((x) => x.key === s.sector) || SECTORS[0];
            const Icon = Sec.icon;
            const sev = SEVERITY[s.severity] || SEVERITY.info;
            return (
              <div key={s.id} className="grid grid-cols-12 items-center gap-3 p-3 border border-[#1a1a1a] hover:border-gold transition-colors" data-testid={`signal-${s.id}`}>
                <div className="col-span-2 md:col-span-1">
                  <Icon className="w-5 h-5 text-gold" />
                </div>
                <div className="col-span-3 md:col-span-2">
                  <span className="label-tag">{Sec.label}</span>
                </div>
                <div className="col-span-7 md:col-span-7 text-sm font-bold">{s.headline}</div>
                <div className="col-span-6 md:col-span-1 label-tag text-[#888]">{s.region || "—"}</div>
                <div className="col-span-6 md:col-span-1 flex justify-end">
                  <span className={`label-tag inline-flex items-center gap-1 px-2 py-1 border ${sev.color}`}>
                    <AlertTriangle className="w-3 h-3" /> {sev.label}
                  </span>
                </div>
              </div>
            );
          })}
          {signals.length === 0 && <div className="text-center text-[#666] py-10">No signals logged yet.</div>}
        </div>
      </Panel>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="label-tag block mb-1">{label}</label>
      {children}
    </div>
  );
}
